import React, { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { EyeOutlined } from "@ant-design/icons";
import { Button, Dropdown, Space, Tag, Tooltip } from "antd";
import { DotVerticalIcon, SendIcon } from "assets/icons";
import { saveAs } from "file-saver";
import moment from "moment/moment";
import { AuthInfo } from "stores/slices/auth";
import { setHeaderTitle } from "stores/slices/layout";
import {
  doExportRecipients,
  doGetBroadcastDetail,
  doGetMessagePastPage,
} from "stores/slices/message";

import ModalForm from "components/forms/components/modalForm";
import AppTable from "components/table";

import { USER_TYPE } from "../../../constants/appConstant";

import MessageDetailForm from "./messageDetailForm";

const MessagePastPage = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const authInfo = useSelector(AuthInfo);

  const [searchParam, setSearchParam] = useState({
    pageNumber: 1,
    pageSize: 10,
    future: false,
    workspaceId: authInfo.workspaceId,
    sortDirection: "DESC",
    sortField: "createdTime",
  });
  const [columns, setColumns] = useState([]);
  const [isView, setIsview] = useState(false);
  const [selectedMessageDetail, setSelectedMessageDetail] = useState({});
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    dispatch(setHeaderTitle(["messages", "pastBroadcast"]));
    generateTableColumns();
  }, []);

  const handleViewDetail = async data => {
    const result = await dispatch(doGetBroadcastDetail({ id: data.id }));
    const { payload } = result;
    if (typeof payload === "object" && payload.messageId) {
      await setSelectedMessageDetail({
        ...payload,
        messageId: payload.messageId,
        address: data.addressNumber,
        timezone: data.timezone,
        fromUserEmail: data.fromUserEmail,
      });
      await setIsview(true);
    }
  };

  const handleSubmitDownload = async () => {
    setLoading(true);
    const result = await dispatch(
      doExportRecipients({
        messageId: selectedMessageDetail.messageId,
        statuses: ["DELIVERED", "FAIL", "READ"],
      })
    );
    setLoading(false);
    if (result.payload) {
      const blob = new Blob(["\ufeff", result.payload], {
        type: "text/csv;charset=utf-8",
      });
      // saveAs(blob, `Recipients_${moment(new Date()).format("MM_DD_YYYY")}`);
      saveAs(blob, `Recipients_${selectedMessageDetail.messageId}`);
    }
  };

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {
      // statuses: [STATUS_TYPE.ACTIVE],
      workspaceId: authInfo.workspaceId,
    };
    filters.workspaceName &&
      (newSearchParam = {
        ...newSearchParam,
        workspaceName: filters.workspaceName[0],
      });
    filters.userName &&
      (newSearchParam = {
        ...newSearchParam,
        userName: filters.userName[0],
      });
    filters.address &&
      (newSearchParam = {
        ...newSearchParam,
        addressNumber: filters.address[0],
      });
    filters.message &&
      (newSearchParam = {
        ...newSearchParam,
        message: filters.message[0],
      });
    filters.broadcastType &&
      filters.broadcastType.indexOf("SEND_NOW") !== -1 &&
      filters.broadcastType.indexOf("DRAFT") === -1 &&
      filters.broadcastType.indexOf("REPEAT") === -1 &&
      filters.broadcastType.indexOf("SEND_LATER") === -1 &&
      (newSearchParam = {
        ...newSearchParam,
        broadcastTypes: "SEND_NOW",
      });
    filters.broadcastType &&
      filters.broadcastType.indexOf("SEND_LATER") !== -1 &&
      filters.broadcastType.indexOf("DRAFT") === -1 &&
      filters.broadcastType.indexOf("REPEAT") === -1 &&
      filters.broadcastType.indexOf("SEND_NOW") === -1 &&
      (newSearchParam = {
        ...newSearchParam,
        broadcastTypes: "SEND_LATER",
      });
    filters.broadcastType &&
      filters.broadcastType.indexOf("DRAFT") !== -1 &&
      filters.broadcastType.indexOf("SEND_LATER") === -1 &&
      filters.broadcastType.indexOf("REPEAT") === -1 &&
      filters.broadcastType.indexOf("SEND_NOW") === -1 &&
      (newSearchParam = {
        ...newSearchParam,
        broadcastTypes: "DRAFT",
      });
    filters.broadcastType &&
      filters.broadcastType.indexOf("REPEAT") !== -1 &&
      filters.broadcastType.indexOf("DRAFT") === -1 &&
      filters.broadcastType.indexOf("SEND_LATER") === -1 &&
      filters.broadcastType.indexOf("SEND_NOW") === -1 &&
      (newSearchParam = {
        ...newSearchParam,
        broadcastTypes: "REPEAT",
      });
    filters.dateAndTime &&
      filters.dateAndTime[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromDate: moment(filters.dateAndTime[0].toString()).format(
          "MM/DD/YYYY"
        ),
      });
    filters.dateAndTime &&
      filters.dateAndTime[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toDate: moment(filters.dateAndTime[1].toString()).format("MM/DD/YYYY"),
      });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortField: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortField: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      future: false,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setSearchParam(newSearchParam);
  };

  const handleFormatDateLocale = (date, timezone) => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date)
        .tz(timezone || moment.tz.guess())
        .locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const actionOptionItems = record => {
    return [
      {
        key: "1",
        label: <span>{t("message.view")}</span>,
        icon: <EyeOutlined style={{ fontSize: 20 }} />,
        onClick: () => {
          handleViewDetail(record);
        },
      },
    ];
  };

  const generateTableColumns = () => {
    let cols = [];
    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      cols = [
        {
          title: t("message.workspace"),
          width: 60,
          dataIndex: "workspaceName",
          key: "workspaceName",
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.workspace").toString().toLowerCase(),
          sorter: true,
          sortDirections: ["ascend", "descend"],
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => (
            <>
              {record?.workspaceName ? record?.workspaceName : "-"}
              {record?.workspaceStatus === "DELETE" ? "(DELETED)" : ""}
            </>
          ),
        },
        {
          title: t("message.number"),
          width: 60,
          dataIndex: "addressNumber",
          key: "address",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.number").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("message.customers"),
          width: 40,
          dataIndex: "totalCustomer",
          key: "totalCustomer",
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("message.message"),
          width: 60,
          dataIndex: "message",
          key: "message",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.message").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => (
            <Tooltip title={record.message}>{record.message}</Tooltip>
          ),
        },
        {
          title: t("message.dateAndTime"),
          width: 60,
          dataIndex: "dateAndTime",
          key: "dateAndTime",
          isFilterable: true,
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => (
            <>{handleFormatDateLocale(record.createdTime, record.timezone)}</>
          ),
        },

        {
          title: t("message.status"),
          width: 40,
          dataIndex: "broadcastScheduleStatus",
          key: "broadcastScheduleStatus",
          render: (_, record) => (
            <Tag
              color={
                record.broadcastScheduleStatus === "SCHEDULED"
                  ? "#DBEBFD"
                  : "#FDF8EA"
              }
              bordered={false}
            >
              <div
                style={{
                  display: "flex",
                  alignContent: "center",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  color:
                    record.broadcastScheduleStatus === "SCHEDULED"
                      ? "#337AC9"
                      : "#C48F07",
                  padding: 5,
                }}
              >
                {record.broadcastScheduleStatus === "SCHEDULED" ? (
                  <>
                    <SendIcon color={"#337AC9"} />{" "}
                    <span style={{ marginLeft: 8 }}>
                      {t(`message.${record.broadcastScheduleStatus}`)}
                    </span>
                  </>
                ) : (
                  <>
                    <SendIcon color={"#C48F07"} />
                    <span style={{ marginLeft: 8 }}>
                      {t(`message.${record.broadcastScheduleStatus}`)}
                    </span>
                  </>
                )}
              </div>
            </Tag>
          ),
        },
        {
          title: t("message.actions"),
          width: 25,
          key: "operation",
          render: (_, record) => {
            return (
              <>
                <Space size="middle">
                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: actionOptionItems(record),
                    }}
                    placement="bottomRight"
                  >
                    <Button type="text" shape="circle" size="middle">
                      <DotVerticalIcon />
                    </Button>
                  </Dropdown>
                </Space>
              </>
            );
          },
        },
      ];
    } else {
      cols = [
        {
          title: t("message.username"),
          width: 60,
          dataIndex: "userName",
          key: "userName",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.username").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => {
            if (record.fromUserFirstName || record.fromUserLastName) {
              return (
                <>{`${
                  record.fromUserFirstName ? record.fromUserFirstName : ""
                } ${record.fromUserLastName ? record.fromUserLastName : ""}`}</>
              );
            } else if (
              !record.fromUserFirstName &&
              !record.fromUserLastName &&
              !record.fromUserEmail
            ) {
              return <>{t("message.deletedUser")}</>;
            } else {
              return (
                <>{`${record.fromUserEmail ? record.fromUserEmail : ""}`}</>
              );
            }
          },
        },
        {
          title: t("message.dateAndTime"),
          width: 60,
          dataIndex: "dateAndTime",
          key: "dateAndTime",
          isFilterable: true,
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => (
            <>{handleFormatDateLocale(record.createdTime, record.timezone)}</>
          ),
        },
        {
          title: t("message.number"),
          width: 60,
          dataIndex: "addressNumber",
          key: "address",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.number").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("message.customers"),
          width: 40,
          dataIndex: "totalCustomer",
          key: "totalCustomer",
          ellipsis: {
            showTitle: false,
          },
        },
        {
          title: t("message.message"),
          width: 60,
          dataIndex: "message",
          key: "message",
          sorter: true,
          sortDirections: ["ascend", "descend"],
          isSearchable: true,
          searchPlaceholder:
            "Search by " + t("message.message").toString().toLowerCase(),
          ellipsis: {
            showTitle: false,
          },
          render: (_, record) => (
            <Tooltip title={record.message}>{record.message}</Tooltip>
          ),
        },
        {
          title: t("message.status"),
          width: 40,
          dataIndex: "broadcastScheduleStatus",
          key: "broadcastScheduleStatus",
          render: (_, record) => (
            <Tag
              color={
                record.broadcastScheduleStatus === "SCHEDULED"
                  ? "#DBEBFD"
                  : "#FDF8EA"
              }
              bordered={false}
            >
              <div
                style={{
                  display: "flex",
                  alignContent: "center",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  color:
                    record.broadcastScheduleStatus === "SCHEDULED"
                      ? "#337AC9"
                      : "#C48F07",
                  padding: 5,
                }}
              >
                {record.broadcastScheduleStatus === "SCHEDULED" ? (
                  <>
                    <SendIcon color={"#337AC9"} />{" "}
                    <span style={{ marginLeft: 8 }}>
                      {t(`message.${record.broadcastScheduleStatus}`)}
                    </span>
                  </>
                ) : (
                  <>
                    <SendIcon color={"#C48F07"} />
                    <span style={{ marginLeft: 8 }}>
                      {t(`message.${record.broadcastScheduleStatus}`)}
                    </span>
                  </>
                )}
              </div>
            </Tag>
          ),
        },
        {
          title: t("message.actions"),
          width: 25,
          key: "operation",
          render: (_, record) => {
            return (
              <>
                <Space size="middle">
                  {/* <Button
                    type="text"
                    shape="circle"
                    size="middle"
                    onClick={() => {
                      handleViewDetail(record);
                    }}
                    style={{
                      marginRight: 8,
                    }}
                  >
                    <EyeOutlined />
                  </Button> */}

                  <Dropdown
                    trigger={["click"]}
                    menu={{
                      items: actionOptionItems(record),
                    }}
                    placement="bottomRight"
                    disabled={
                      record.fromUserEmail === null &&
                      record.fromUserFirstName === null &&
                      record.fromUserLastName === null
                    }
                  >
                    <Button type="text" shape="circle" size="middle">
                      <DotVerticalIcon />
                    </Button>
                  </Dropdown>
                </Space>
              </>
            );
          },
        },
      ];
    }

    setColumns(cols);
  };

  return (
    <div>
      <div style={{ witdh: "100%" }}>
        <AppTable
          api={doGetMessagePastPage(searchParam)}
          columns={columns}
          searchParam={searchParam}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          handleTableChange={handleTableChange}
          setSearchParam={setSearchParam}
        />
      </div>
      <ModalForm
        title={t("message.messageDetail")}
        isOpen={isView}
        onCancel={() => {
          setIsview(false);
        }}
        customWidth={900}
        labelSubmit={t("button.download")}
        onSubmit={handleSubmitDownload}
        footer={false}
        destroyOnClose={true}
        maskClosable={false}
      >
        <MessageDetailForm
          messageDetail={selectedMessageDetail}
          isView={isView}
          onCancel={() => {
            setIsview(false);
          }}
          onDownload={handleSubmitDownload}
          isRecipient={true}
          loading={loading}
          setLoading={setLoading}
          setIsview={setIsview}
        />
      </ModalForm>
    </div>
  );
};

export default MessagePastPage;
